import { useState, useRef } from "react";
import api from "@/lib/api";
import OnboardingBanner from "@/components/OnboardingBanner";
import { useTasks, useProperties, useInvalidate } from "@/lib/queries";
import { usePropertyFilter } from "@/context/PropertyFilter";
import { useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Broom, MagnifyingGlass, Wrench, CheckCircle,
  Trash, PencilSimple, X, CalendarBlank, User, Buildings,
  Warning, CurrencyInr, CheckFat, FunnelSimple, Camera, Image, SpinnerGap,
  CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { EXPENSE_CATEGORIES } from "@/lib/utils";

const TYPE_META = {
  cleaning:    { icon: Broom,           label: "Cleaning",     color: "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700" },
  inspection:  { icon: MagnifyingGlass, label: "Inspection",   color: "bg-violet-50 text-violet-600 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-700" },
  maintenance: { icon: Wrench,          label: "Maintenance",  color: "bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700" },
  general:     { icon: CheckCircle,     label: "General",      color: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600" },
};
const PRIORITY_META = {
  urgent: { color: "text-red-600 bg-red-50 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700",    dot: "bg-red-500" },
  high:   { color: "text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700", dot: "bg-orange-400" },
  normal: { color: "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700", dot: "bg-blue-400" },
  low:    { color: "text-slate-600 bg-slate-50 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600", dot: "bg-slate-300" },
};
const STATUS_COLS = [
  { id: "pending",     label: "Pending",     color: "text-slate-600 dark:text-slate-300", bg: "bg-slate-50" },
  { id: "in_progress", label: "In Progress", color: "text-blue-600",  bg: "bg-blue-50" },
  { id: "done",        label: "Done",        color: "text-green-600", bg: "bg-green-50" },
];

const TYPE_TO_EXPENSE_CAT = {
  cleaning: "Cleaning", maintenance: "Maintenance", inspection: "Supplies", general: "Other",
};

const EMPTY_FORM = { title: "", type: "cleaning", priority: "normal", property_id: "",
                     due_date: "", due_time: "11:00", assignee: "", assignee_phone: "", assignee_email: "", notes: "" };

const EMPTY_EXP = { amount: "", category: "Maintenance", description: "", date: new Date().toISOString().slice(0,10) };

function deadlineInfo(due_date, due_time, status) {
  if (!due_date) return null;
  if (status === "done") return null;
  const now = new Date();
  const deadline = new Date(`${due_date}T${due_time || "23:59"}:00`);
  const diffMs = deadline - now;
  const diffDays = Math.ceil(diffMs / 86400000);
  if (diffMs < 0) {
    const overdueDays = Math.abs(Math.floor(diffMs / 86400000));
    return {
      label: overdueDays === 0 ? "Overdue today" : `Overdue ${overdueDays}d`,
      color: "text-red-600 bg-red-50 border-red-200",
      icon: Warning,
    };
  }
  if (diffDays === 0) return { label: "Due today",     color: "text-red-600 bg-red-50 border-red-200",    icon: Warning };
  if (diffDays === 1) return { label: "Due tomorrow",  color: "text-orange-600 bg-orange-50 border-orange-200", icon: CalendarBlank };
  if (diffDays <= 3)  return { label: `${diffDays}d left`, color: "text-amber-600 bg-amber-50 border-amber-200",  icon: CalendarBlank };
  return { label: `${diffDays}d left`, color: "text-slate-500 bg-slate-50 border-slate-200", icon: CalendarBlank };
}

function TaskCard({ task, properties, onUpdate, onDelete, onEdit, isMine }) {
  const typeMeta = TYPE_META[task.type] || TYPE_META.general;
  const prioMeta = PRIORITY_META[task.priority] || PRIORITY_META.normal;
  const prop = properties.find(p => (p.property_id || p.id) === task.property_id);
  const Icon = typeMeta.icon;
  const dl = deadlineInfo(task.due_date, task.due_time, task.status);
  const DeadlineIcon = dl?.icon;

  return (
    <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }}
      className={`bg-white dark:bg-slate-800 rounded-xl border shadow-sm p-4 space-y-3 card-hover ${isMine ? "border-teal-400 dark:border-teal-500 ring-1 ring-teal-400/30" : "border-slate-100"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border ${typeMeta.color}`}>
            <Icon size={13} weight="duotone" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 dark:text-white leading-snug">{task.title}</p>
            {prop && <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><Buildings size={10} />{prop.name}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onEdit(task)} className="text-slate-400 hover:text-amber-400 transition p-1" title="Edit task">
            <PencilSimple size={13} />
          </button>
          <button onClick={() => onDelete(task.task_id)} className="text-slate-400 hover:text-red-400 transition p-1" title="Delete task">
            <Trash size={13} />
          </button>
        </div>
      </div>

      {/* Deadline banner */}
      {dl && (
        <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs font-semibold ${dl.color}`}>
          <span className="flex items-center gap-1.5">
            <DeadlineIcon size={11} weight="fill" />
            {dl.label}
          </span>
          <span className="font-normal opacity-70">
            {task.due_date}{task.due_time ? ` · ${task.due_time}` : ""}
          </span>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${prioMeta.color}`}>
          {task.priority.toUpperCase()}
        </span>
        {task.assignee && (
          <span className="text-[10px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 border border-slate-100 dark:border-slate-600 px-2 py-0.5 rounded-full flex items-center gap-1">
            <User size={9} />{task.assignee}
          </span>
        )}
        {isMine && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-700">
            My task
          </span>
        )}
      </div>

      {task.notes && <p className="text-xs text-slate-400 leading-relaxed">{task.notes}</p>}

      {task.photos?.length > 0 && (
        <div className="flex items-center gap-1.5 text-[10px] text-blue-500 font-semibold">
          <Camera size={11} weight="duotone" />
          {task.photos.length} photo{task.photos.length > 1 ? "s" : ""} attached
        </div>
      )}

      <div className="flex gap-1.5 pt-1">
        {STATUS_COLS.map(col => (
          <button key={col.id} onClick={() => onUpdate(task.task_id, { status: col.id })}
            className={`flex-1 py-1 rounded-lg text-[10px] font-medium transition-all border ${
              task.status === col.id
                ? col.id === "done" ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
                  : col.id === "in_progress" ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700"
                  : "bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500"
                : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-100 dark:border-slate-600 hover:border-slate-200 dark:hover:border-slate-500"
            }`}>
            {col.label}
          </button>
        ))}
      </div>
    </motion.div>
  );
}

export default function Tasks() {
  const { selectedPropertyId, selectedRoomId } = usePropertyFilter();
  const taskFilters = {
    ...(selectedPropertyId ? { property_id: selectedPropertyId } : {}),
    ...(selectedRoomId     ? { room_id: selectedRoomId }         : {}),
  };
  const { data: tasks = [], isLoading: loading } = useTasks(taskFilters);
  const { data: properties = [] } = useProperties();
  const invalidate = useInvalidate();
  const { user: currentUser } = useAuth();
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [filter, setFilter]       = useState("all");
  const [search, setSearch]       = useState("");
  const [donePromptTask, setDonePromptTask] = useState(null);
  const [doneExpAmount, setDoneExpAmount]   = useState("");
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = last, +1 = next, null = all
  const [showAllWeeks, setShowAllWeeks] = useState(true);

  // Edit modal state
  const [editTask, setEditTask]       = useState(null);
  const [editForm, setEditForm]       = useState(EMPTY_FORM);
  const [editTab, setEditTab]         = useState("details");
  const [expForm, setExpForm]         = useState(EMPTY_EXP);
  const [expSaved, setExpSaved]       = useState(false);
  const [editSaving, setEditSaving]   = useState(false);
  const [prevLogged, setPrevLogged]   = useState(0);
  const [taskPhotos, setTaskPhotos]   = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoType, setPhotoType]     = useState("general");
  const [invitingCleaner, setInvitingCleaner] = useState(false);
  const photoInputRef = useRef(null);

  const createTask = async () => {
    if (!form.title.trim()) { toast.error("Task title is required"); return; }
    setSaving(true);
    try {
      await api.post("/tasks", form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      toast.success("Task created");
      invalidate.afterTaskMutation();
    } catch { toast.error("Failed to create task"); }
    finally { setSaving(false); }
  };

  const updateTask = async (taskId, updates) => {
    await api.put(`/tasks/${taskId}`, updates);
    invalidate.afterTaskMutation();
    if (updates.status === "done") {
      const task = tasks.find(t => t.task_id === taskId);
      if (task?.property_id) {
        try {
          const existing = await api.get(`/expenses?task_id=${taskId}`).then(r => r.data);
          const total = (Array.isArray(existing) ? existing : []).reduce((s, e) => s + (e.amount || 0), 0);
          if (total === 0) {
            setDoneExpAmount("");
            setDonePromptTask(task);
          }
        } catch { /* non-critical */ }
      }
    }
  };

  const logDoneExpense = async () => {
    const amount = parseFloat(doneExpAmount);
    if (isNaN(amount) || amount <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post("/expenses", {
        task_id:     donePromptTask.task_id,
        property_id: donePromptTask.property_id,
        amount,
        category:    TYPE_TO_EXPENSE_CAT[donePromptTask.type] || "Other",
        description: donePromptTask.title,
        date:        donePromptTask.due_date || new Date().toISOString().slice(0, 10),
      });
      toast.success(`₹${amount.toLocaleString("en-IN")} logged to Expenses`);
      setDonePromptTask(null);
    } catch { toast.error("Failed to log expense"); }
  };

  const deleteTask = async (taskId) => {
    await api.delete(`/tasks/${taskId}`);
    toast.success("Task deleted");
    invalidate.afterTaskMutation();
  };

  const openEdit = async (task) => {
    setEditTask(task);
    setEditForm({
      title: task.title || "", type: task.type || "cleaning",
      priority: task.priority || "normal", property_id: task.property_id || "",
      due_date: task.due_date || "", due_time: task.due_time || "11:00",
      assignee: task.assignee || "", assignee_phone: task.assignee_phone || "",
      assignee_email: task.assignee_email || "",
      notes: task.notes || "",
    });
    setExpForm({
      ...EMPTY_EXP,
      category: TYPE_TO_EXPENSE_CAT[task.type] || "Other",
      description: task.title || "",
      date: new Date().toISOString().slice(0, 10),
    });
    setExpSaved(false);
    setEditTab("details");
    setPrevLogged(0);
    setTaskPhotos(task.photos || []);
    try {
      const existing = await api.get(`/expenses?task_id=${task.task_id}`).then(r => r.data);
      if (Array.isArray(existing)) {
        const total = existing.reduce((sum, e) => sum + (e.amount || 0), 0);
        setPrevLogged(total);
        if (total > 0) setExpForm(f => ({ ...f, amount: String(total) }));
      }
    } catch { /* non-critical */ }
  };

  const closeEdit = () => {
    setEditTask(null);
    setTaskPhotos([]);
  };

  const inviteAsCleaner = async () => {
    const email = editForm.assignee_email?.trim();
    if (!email) { toast.error("Enter assignee email first"); return; }
    setInvitingCleaner(true);
    try {
      await api.post("/team/invite", { email, role: "maintenance" });
      toast.success(`Invite sent to ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send invite");
    } finally { setInvitingCleaner(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const isVideo = (file.type || "").startsWith("video/");
    const maxBytes = isVideo ? 60 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(isVideo ? "Video is over 60 MB. Trim it or record at a lower resolution." : "Image is over 10 MB.");
      return;
    }
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("photo_type", photoType);
      const { data: photo } = await api.post(`/tasks/${editTask.task_id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTaskPhotos(p => [...p, photo]);
      invalidate.afterTaskMutation();
      toast.success(isVideo ? "Video uploaded" : "Photo uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setPhotoUploading(false); }
  };

  const deletePhoto = async (photoId) => {
    try {
      await api.delete(`/tasks/${editTask.task_id}/photos/${photoId}`);
      setTaskPhotos(p => p.filter(x => x.photo_id !== photoId));
      invalidate.afterTaskMutation();
      toast.success("Photo removed");
    } catch { toast.error("Failed to remove photo"); }
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) { toast.error("Title is required"); return; }
    setEditSaving(true);
    try {
      await api.put(`/tasks/${editTask.task_id}`, editForm);
      setEditTask(null);
      toast.success("Task updated");
      invalidate.afterTaskMutation();
    } catch { toast.error("Failed to save"); }
    finally { setEditSaving(false); }
  };

  const logExpense = async () => {
    const newTotal = parseFloat(expForm.amount);
    if (isNaN(newTotal) || newTotal < 0) { toast.error("Enter a valid total cost"); return; }
    if (!editTask.property_id) { toast.error("Task has no property linked — set one in Details first"); return; }
    if (newTotal === prevLogged) { toast("No change in cost — nothing to update"); return; }
    setEditSaving(true);
    try {
      // 1. Fetch all existing expense entries for this task
      const existing = await api.get(`/expenses?task_id=${editTask.task_id}`).then(r => r.data);
      // 2. Delete them all
      await Promise.all(
        (Array.isArray(existing) ? existing : []).map(e => api.delete(`/expenses/${e.id}`))
      );
      // 3. If new total > 0, create one clean consolidated entry
      if (newTotal > 0) {
        await api.post("/expenses", {
          task_id: editTask.task_id,
          property_id: editTask.property_id,
          amount: newTotal,
          category: expForm.category,
          description: expForm.description || editTask.title,
          date: expForm.date,
        });
      }
      const prev = prevLogged;
      setPrevLogged(newTotal);
      setExpSaved(true);
      if (prev === 0) toast.success(`₹${newTotal.toLocaleString("en-IN")} logged to Expenses`);
      else if (newTotal > prev) toast.success(`Cost updated ₹${prev.toLocaleString("en-IN")} â†’ ₹${newTotal.toLocaleString("en-IN")}`);
      else toast.success(`Cost reduced ₹${prev.toLocaleString("en-IN")} â†’ ₹${newTotal.toLocaleString("en-IN")}`);
    } catch { toast.error("Failed to update expense"); }
    finally { setEditSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition";

  // Week nav helpers
  const getWeekBounds = (offset) => {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { start: monday, end: sunday };
  };
  const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const weekBounds = getWeekBounds(weekOffset);
  const weekLabel = (() => {
    const fmt = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    return `${fmt(weekBounds.start)} — ${fmt(weekBounds.end)}`;
  })();

  const myEmail = currentUser?.email?.toLowerCase();

  const filtered = tasks.filter(t => {
    if (!showAllWeeks && t.due_date) {
      if (t.due_date < toDateStr(weekBounds.start) || t.due_date > toDateStr(weekBounds.end)) return false;
    }
    if (filter !== "all" && t.status !== filter) return false;
    if (selectedPropertyId && t.property_id !== selectedPropertyId) return false;
    if (selectedRoomId     && t.room_id     !== selectedRoomId)     return false;
    if (search && !t.title?.toLowerCase().includes(search.toLowerCase()) &&
        !t.assignee?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }).sort((a, b) => {
    const aMine = myEmail && a.assignee_email?.toLowerCase() === myEmail ? 1 : 0;
    const bMine = myEmail && b.assignee_email?.toLowerCase() === myEmail ? 1 : 0;
    return bMine - aMine;
  });

  const counts = {
    all: filtered.length,
    pending:     filtered.filter(t => t.status === "pending").length,
    in_progress: filtered.filter(t => t.status === "in_progress").length,
    done:        filtered.filter(t => t.status === "done").length,
  };

  return (
    <div className="space-y-6">
      <OnboardingBanner pageKey="tasks" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-amber-400 tracking-tight">Task Board</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage cleaning, inspection and maintenance tasks for your properties.</p>
        </div>
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition shadow-sm">
          <Plus size={15} weight="bold" /> New Task
        </button>
      </div>

      {/* Week navigator */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => { setShowAllWeeks(false); setWeekOffset(0); }}
          className={`px-3 py-1.5 text-xs font-semibold rounded-xl transition-all ${
            !showAllWeeks && weekOffset === 0
              ? "bg-amber-400 text-white shadow-sm"
              : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
          }`}>
          This Week
        </button>
        <div className={`flex items-center gap-1 ${showAllWeeks ? "opacity-40 pointer-events-none" : ""}`}>
          <button onClick={() => setWeekOffset(o => o - 1)}
            className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 transition">
            <CaretLeft size={13} weight="bold" />
          </button>
          <span className="text-xs font-medium text-slate-700 dark:text-slate-200 min-w-[140px] text-center">
            {weekLabel}
          </span>
          <button onClick={() => setWeekOffset(o => o + 1)}
            className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 transition">
            <CaretRight size={13} weight="bold" />
          </button>
        </div>
        <button onClick={() => setShowAllWeeks(v => !v)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-xl transition-all ml-1 ${
            showAllWeeks
              ? "bg-slate-700 text-white dark:bg-slate-200 dark:text-slate-800"
              : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
          }`}>
          All Time
        </button>
      </div>

      {/* Filter tabs + search + property filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex gap-2">
          {[["all", "All"], ["pending", "Pending"], ["in_progress", "In Progress"], ["done", "Done"]].map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                filter === id ? "bg-amber-400 text-white shadow-sm" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}>
              {label} <span className="ml-1 opacity-70">{counts[id]}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 ml-auto">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search tasks…"
              className="pl-8 pr-3 py-1.5 text-xs rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 w-44" />
          </div>
        </div>
      </div>

      {/* Create task form */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="bg-white dark:bg-slate-800 rounded-2xl border border-amber-200 shadow-md p-5 space-y-4">
            <div className="flex items-center justify-between mb-1">
              <p className="font-semibold text-slate-800 dark:text-white text-sm">New Task</p>
              <button onClick={() => setShowForm(false)}><X size={16} className="text-slate-400" /></button>
            </div>
            <input placeholder="Task title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className={inputCls}>
                {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className={inputCls}>
                {Object.entries(PRIORITY_META).map(([k]) => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.property_id} onChange={e => setForm(f => ({ ...f, property_id: e.target.value }))} className={inputCls}>
                <option value="">Select property</option>
                {properties.map(p => <option key={p.property_id || p.id} value={p.property_id || p.id}>{p.name}</option>)}
              </select>
              <div className="flex gap-2">
                <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className={inputCls} />
                <input type="time" value={form.due_time} onChange={e => setForm(f => ({ ...f, due_time: e.target.value }))} className={inputCls + " w-28"} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Assignee name" value={form.assignee} onChange={e => setForm(f => ({ ...f, assignee: e.target.value }))} className={inputCls} />
              <input placeholder="Assignee phone (for WhatsApp)" value={form.assignee_phone} onChange={e => setForm(f => ({ ...f, assignee_phone: e.target.value }))} className={inputCls} />
            </div>
            <input placeholder="Assignee email (to invite as cleaner)" value={form.assignee_email} onChange={e => setForm(f => ({ ...f, assignee_email: e.target.value }))} className={inputCls} />
            <textarea placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} className={inputCls + " resize-none"} />
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition">Cancel</button>
              <button onClick={createTask} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50">
                {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Create Task"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-32 skeleton rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <CheckCircle size={36} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No tasks yet. Click "New Task" to create one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence mode="popLayout">
            {filtered.map(task => (
              <TaskCard key={task.task_id} task={task} properties={properties}
                onUpdate={updateTask} onDelete={deleteTask} onEdit={openEdit}
                isMine={!!(myEmail && task.assignee_email?.toLowerCase() === myEmail)} />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* ── Done → Expense Prompt ── */}
      <AnimatePresence>
        {donePromptTask && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => setDonePromptTask(null)}>
            <motion.div initial={{ scale: 0.93, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.93, y: 16 }}
              onClick={e => e.stopPropagation()}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                  <CheckFat size={20} className="text-green-600 dark:text-green-400" weight="fill" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 dark:text-white">Task completed!</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate max-w-[200px]">{donePromptTask.title}</p>
                </div>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-3">
                Log the cost? It will be added to <span className="font-semibold">Expenses</span> automatically.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-semibold text-slate-500">₹</span>
                <input
                  type="number"
                  placeholder="Amount (e.g. 800)"
                  value={doneExpAmount}
                  onChange={e => setDoneExpAmount(e.target.value)}
                  autoFocus
                  onKeyDown={e => e.key === "Enter" && logDoneExpense()}
                  className="flex-1 px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setDonePromptTask(null)}
                  className="flex-1 py-2.5 text-sm text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl font-medium transition">
                  Skip
                </button>
                <button onClick={logDoneExpense}
                  className="flex-1 py-2.5 text-sm text-white bg-amber-400 hover:bg-amber-500 rounded-xl font-semibold transition flex items-center justify-center gap-1.5">
                  <CurrencyInr size={14} weight="bold" /> Log Expense
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Edit Modal ── */}
      <AnimatePresence>
        {editTask && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/30 backdrop-blur-sm"
            onClick={e => { if (e.target === e.currentTarget) closeEdit(); }}>
            <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 16 }}
              transition={{ type: "spring", stiffness: 400, damping: 28 }}
              className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-100 dark:border-slate-700 flex flex-col"
              style={{ maxHeight: "calc(100vh - 3rem)" }}>

              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">Edit Task</p>
                <button onClick={closeEdit} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition">
                  <X size={16} />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-100 dark:border-slate-700">
                {[["details", "Task Details"], ["expense", "Log Expense"], ["photos", `Photos${taskPhotos.length > 0 ? ` (${taskPhotos.length})` : ""}`]].map(([id, label]) => (
                  <button key={id} onClick={() => setEditTab(id)}
                    className={`flex-1 py-2.5 text-xs font-semibold transition border-b-2 ${
                      editTab === id
                        ? "border-amber-400 text-amber-600"
                        : "border-transparent text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="p-5 space-y-3 overflow-y-auto min-h-0">
                {editTab === "details" ? (
                  <>
                    <input placeholder="Task title *" value={editForm.title}
                      onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                      className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <select value={editForm.type} onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))} className={inputCls}>
                        {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select>
                      <select value={editForm.priority} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))} className={inputCls}>
                        {Object.entries(PRIORITY_META).map(([k]) => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <select value={editForm.property_id} onChange={e => setEditForm(f => ({ ...f, property_id: e.target.value }))} className={inputCls}>
                        <option value="">Select property</option>
                        {properties.map(p => <option key={p.property_id || p.id} value={p.property_id || p.id}>{p.name}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <input type="date" value={editForm.due_date} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} className={inputCls} />
                        <input type="time" value={editForm.due_time} onChange={e => setEditForm(f => ({ ...f, due_time: e.target.value }))} className={inputCls + " w-28"} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input placeholder="Assignee name" value={editForm.assignee}
                        onChange={e => setEditForm(f => ({ ...f, assignee: e.target.value }))} className={inputCls} />
                      <input placeholder="Assignee phone (WhatsApp)" value={editForm.assignee_phone}
                        onChange={e => setEditForm(f => ({ ...f, assignee_phone: e.target.value }))} className={inputCls} />
                    </div>
                    <div className="flex gap-2">
                      <input placeholder="Assignee email" value={editForm.assignee_email}
                        onChange={e => setEditForm(f => ({ ...f, assignee_email: e.target.value }))}
                        className={inputCls + " flex-1"} />
                      <button onClick={inviteAsCleaner} disabled={invitingCleaner || !editForm.assignee_email}
                        className="px-3 py-2 text-xs font-semibold bg-teal-500 hover:bg-teal-600 text-white rounded-xl transition disabled:opacity-40 whitespace-nowrap shrink-0">
                        {invitingCleaner ? "Sending…" : "Invite as Cleaner"}
                      </button>
                    </div>
                    <textarea placeholder="Notes (optional)" value={editForm.notes}
                      onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                      rows={2} className={inputCls + " resize-none"} />
                    <div className="flex justify-end gap-3 pt-1">
                      <button onClick={closeEdit} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition">Cancel</button>
                      <button onClick={saveEdit} disabled={editSaving}
                        className="flex items-center gap-2 px-5 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50">
                        {editSaving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Save Changes"}
                      </button>
                    </div>
                  </>
                ) : editTab === "expense" ? (
                  <>
                    {expSaved ? (
                      <div className="flex flex-col items-center justify-center py-10 text-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                          <CheckFat size={24} weight="fill" className="text-green-500" />
                        </div>
                        <p className="font-semibold text-slate-700 text-sm">Expense updated</p>
                        <p className="text-xs text-slate-400">
                          Total in Expenses: <span className="font-semibold text-slate-700">₹{prevLogged.toLocaleString("en-IN")}</span>
                        </p>
                        <button onClick={() => { setExpForm(f => ({ ...f, amount: String(prevLogged) })); setExpSaved(false); }}
                          className="text-xs text-amber-500 hover:text-amber-600 font-semibold mt-1">
                          Edit cost again
                        </button>
                      </div>
                    ) : (
                      <>
                        {/* Current vs new visual */}
                        {prevLogged > 0 && (
                          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3">
                            <div className="flex-1 text-center">
                              <p className="text-[10px] text-slate-400 mb-0.5">CURRENT</p>
                              <p className="text-base font-bold text-slate-700 dark:text-slate-200">₹{prevLogged.toLocaleString("en-IN")}</p>
                            </div>
                            <div className="text-slate-400 dark:text-slate-500 text-lg">→</div>
                            <div className="flex-1 text-center">
                              <p className="text-[10px] text-slate-400 mb-0.5">NEW TOTAL</p>
                              <p className="text-base font-bold text-amber-600">
                                {expForm.amount ? `₹${parseFloat(expForm.amount || 0).toLocaleString("en-IN")}` : "—"}
                              </p>
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1 block">
                            {prevLogged > 0 ? "UPDATED TOTAL COST (₹)" : "TOTAL COST (₹) *"}
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                            <input type="number" placeholder={prevLogged > 0 ? String(prevLogged) : "0"}
                              value={expForm.amount}
                              onChange={e => setExpForm(f => ({ ...f, amount: e.target.value }))}
                              className={inputCls + " pl-7 text-lg font-semibold"} autoFocus />
                          </div>
                          {expForm.amount && parseFloat(expForm.amount) !== prevLogged && (
                            <p className={`text-[11px] mt-1.5 font-medium ${parseFloat(expForm.amount) > prevLogged ? "text-green-600" : "text-red-500"}`}>
                              {parseFloat(expForm.amount) > prevLogged
                                ? `+₹${(parseFloat(expForm.amount) - prevLogged).toLocaleString("en-IN")} will be added`
                                : `-₹${(prevLogged - parseFloat(expForm.amount)).toLocaleString("en-IN")} will be removed`}
                            </p>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1 block">CATEGORY</label>
                            <select value={expForm.category} onChange={e => setExpForm(f => ({ ...f, category: e.target.value }))} className={inputCls}>
                              {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1 block">DATE</label>
                            <input type="date" value={expForm.date}
                              onChange={e => setExpForm(f => ({ ...f, date: e.target.value }))}
                              className={inputCls} />
                          </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-1">
                          <button onClick={closeEdit} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition">Cancel</button>
                          <button onClick={logExpense} disabled={editSaving}
                            className="flex items-center gap-2 px-5 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition disabled:opacity-50">
                            {editSaving
                              ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                              : <><CurrencyInr size={14} weight="bold" />{prevLogged > 0 ? "Update Cost" : "Log Cost"}</>}
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : editTab === "photos" ? (
                  <div className="space-y-4">
                    {/* Photo type selector */}
                    <div className="flex gap-2">
                      {[["before", "Before", "bg-orange-100 text-orange-600 border-orange-300"], ["after", "After", "bg-green-100 text-green-600 border-green-300"], ["general", "General", "bg-slate-100 text-slate-600 border-slate-300"]].map(([val, label, activeCls]) => {
                        const n = taskPhotos.filter(p => (p.photo_type || "general") === val).length;
                        return (
                        <button key={val} onClick={() => setPhotoType(val)}
                          className={`flex-1 py-1.5 text-xs font-semibold rounded-xl border transition ${photoType === val ? activeCls : "border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500 hover:border-slate-300"}`}>
                          {label}{n > 0 ? ` (${n})` : ""}
                        </button>
                        );
                      })}
                    </div>
                    {/* Upload button */}
                    <input ref={photoInputRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" onChange={uploadPhoto} />
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={photoUploading}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:border-amber-400 hover:text-amber-500 dark:hover:border-amber-500 dark:hover:text-amber-400 transition disabled:opacity-50"
                    >
                      {photoUploading
                        ? <><SpinnerGap size={16} className="animate-spin" /> Uploading…</>
                        : <><Camera size={16} weight="duotone" /> Add {photoType.charAt(0).toUpperCase() + photoType.slice(1)} Photo or Video</>}
                    </button>

                    {/* Photo grid — only the selected tab's photos (before / after / general are separate) */}
                    {taskPhotos.filter(p => (p.photo_type || "general") === photoType).length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 gap-2 text-slate-400">
                        <Image size={32} weight="duotone" className="opacity-30" />
                        <p className="text-xs">No {photoType} photos yet. Upload cleanup proof above.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {taskPhotos.filter(p => (p.photo_type || "general") === photoType).map(photo => (
                          <div key={photo.photo_id} className="relative group rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700">
                            <div className="aspect-square">
                              {photo.media_type === "video" ? (
                                <video
                                  src={photo.url}
                                  controls
                                  playsInline
                                  preload="metadata"
                                  className="w-full h-full object-cover bg-black"
                                />
                              ) : (
                                <img
                                  src={photo.url}
                                  alt={photo.original_filename || "Task photo"}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            {photo.photo_type && photo.photo_type !== "general" && (
                              <span className={`absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${photo.photo_type === "before" ? "bg-orange-500 text-white" : "bg-green-500 text-white"}`}>
                                {photo.photo_type.charAt(0).toUpperCase() + photo.photo_type.slice(1)}
                              </span>
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                              <button
                                onClick={() => deletePhoto(photo.photo_id)}
                                className="p-1.5 rounded-full bg-red-500 text-white hover:bg-red-600 transition"
                                title="Delete photo"
                              >
                                <Trash size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-400 text-center">Saved on upload. Max 10 MB per photo, 60 MB per video. Task photos and videos are kept for 30 days, then deleted automatically.</p>
                    <div className="flex justify-end pt-1">
                      <button onClick={closeEdit}
                        className="px-5 py-2 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold rounded-xl transition">
                        Done
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
